// src/components/BoardPack/pages/kpi_chart_on_street.jsx
import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import NewChartKPI from "@/components/BoardPack/components/charts/NewChartKPI";

const NewChartKPIPage = ({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
}) => {
  const { enqueue } = useQueue();

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const hasFetchedRef             = useRef(false);

  // ── Transform API response → chartData rows ─────────────────────────────
  const transformApiData = (apiData) => {
    const contractNames = Object.keys(apiData?.data ?? {});
    if (!contractNames.length) return [];

    const contractData = apiData.data[contractNames[0]];

    // helper: safely parse "12,277" or "25%" → number
    const parseNum = (val) => {
      if (val == null) return 0;
      return parseFloat(String(val).replace(/[,%]/g, "")) || 0;
    };

    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    // derive years from revenue key — skip non-year keys like "isPercent"
    const revenueObj = contractData?.Monthly_Avg_Daily_Parking_Revenue ?? {};
    const years = Object.keys(revenueObj).filter((k) => /^\d{4}$/.test(k));

    const transformedData = [];

    years.forEach((yr) => {
      MONTHS.forEach((mon) => {
        const revenueRaw = revenueObj?.[yr]?.[mon];
        if (!revenueRaw) return; // skip months with no data

        transformedData.push({
          month:       `${mon}-${yr}`,
          year:        yr,
          revenue:     parseNum(revenueRaw),
          parkingBays: parseNum(contractData?.Total_Parking_Bays?.[yr]?.[mon]),
          transaction: parseNum(contractData?.Monthly_Avg_Daily_Transaction?.[yr]?.[mon]),
          occupancy:   parseNum(contractData?.Parking_Occupancy?.[yr]?.[mon]),
          isRamadan:
            contractData?.Ramadan?.[yr]?.[mon] === true ||
            contractData?.Ramadan?.[yr]?.[mon] === "true",
        });
      });
    });

    return transformedData;
  };

  // ── Fetch guard (same pattern as KpiChart12) ────────────────────────────
  useEffect(() => {
    // if (!year || !month || !contract) return;
    // if (hasFetchedRef.current) return;
    // hasFetchedRef.current = true;
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

    //   const params = {
    //     contract: page?.params?.contract ?? contract,
    //     year,
    //     month,
    //     kpi: page?.params?.kpi,
    //   };

    //   const response = await enqueue(() => apiCall("GET", "/board-pack/page-twelve", params));

    const response = {
    "success": true,
    "meta": {
        "contract": "0020013",
        "year": 2026,
        "prevYear": 2025,
        "month": "Apr",
        "kpi": "on_street"
    },
    "data": {
        "0020013 - TAIF ON STREET": {
            "Monthly_Avg_Daily_Parking_Revenue": {
                "2025": {
                    "Jan": "15,258",
                    "Feb": "19,537",
                    "Mar": "25,241",
                    "Apr": "15,051",
                    "May": "13,512",
                    "Jun": "16,456",
                    "Jul": "15,904",
                    "Aug": "16,526",
                    "Sep": "14,411",
                    "Oct": "13,874",
                    "Nov": "15,047",
                    "Dec": "15,056"
                },
                "2026": {
                    "Jan": "16,192",
                    "Feb": "21,364",
                    "Mar": "22,510",
                    "Apr": "14,157"
                }
            },
            "Total_Parking_Bays": {
                "2025": {
                    "Jan": "2,666",
                    "Feb": "2,666",
                    "Mar": "2,666",
                    "Apr": "2,666",
                    "May": "2,727",
                    "Jun": "2,727",
                    "Jul": "2,727",
                    "Aug": "2,727",
                    "Sep": "2,727",
                    "Oct": "2,727",
                    "Nov": "2,727",
                    "Dec": "2,727"
                },
                "2026": {
                    "Jan": "2,727",
                    "Feb": "2,727",
                    "Mar": "2,727",
                    "Apr": "2,727"
                }
            },
            "Monthly_Avg_Daily_Transaction": {
                "2025": {
                    "Jan": "5,017",
                    "Feb": "6,397",
                    "Mar": "8,431",
                    "Apr": "5,042",
                    "May": "4,831",
                    "Jun": "5,557",
                    "Jul": "5,349",
                    "Aug": "5,462",
                    "Sep": "4,969",
                    "Oct": "4,778",
                    "Nov": "5,151",
                    "Dec": "5,092"
                },
                "2026": {
                    "Jan": "5,389",
                    "Feb": "7,055",
                    "Mar": "7,587",
                    "Apr": "4,816"
                }
            },
            "Parking_Occupancy": {
                "2025": {
                    "Jan": "27%",
                    "Feb": "36%",
                    "Mar": "50%",
                    "Apr": "28%",
                    "May": "26%",
                    "Jun": "30%",
                    "Jul": "30%",
                    "Aug": "31%",
                    "Sep": "26%",
                    "Oct": "26%",
                    "Nov": "27%",
                    "Dec": "27%"
                },
                "isPercent": true,
                "2026": {
                    "Jan": "29%",
                    "Feb": "40%",
                    "Mar": "43%",
                    "Apr": "26%"
                }
            }
        }
    }
};
      if (response?.success) {
        const transformed = transformApiData(response);
        setChartData(transformed);
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching on-street data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;

  return (
    <NewChartKPI
      chartData={chartData}
      title={page?.title}
      subtitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      colors={page?.colors}
      labels={page?.labels}
      showDataLabels={page?.showDataLabels ? true : false}
      showRamadanRow={page?.showRamadan || true}
      ramadanborderColor={page?.ramadanBorderColor || "#FFA500"}
      yAxisFormatters={{
        left:  (value) => value.toLocaleString(),
        right: (value) => `${value}%`,
      }}
    />
  );
};

export default NewChartKPIPage;